import { IconButton } from "components";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "components/ui/badge";

import { Pagination } from "components/data-table/Pagination";
import { DeleteIcon, EditIcon } from "components/icons";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useState } from "react";
import { City } from "types";
import { SortOrder } from "@/types/gql/graphql";
import { AddCity } from "./add-city/Panel";
import { PaginatedCitiesProps, usePaginatedCitiesActions } from "./hooks/paginated-cities.hook";
import { TableSkeleton } from "components/data-table";
import { isEmpty } from "helper/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import * as React from "react";

const HeaderCells: { title: string; className: string; sortField?: string }[] = [
  { title: "Ville", className: "text-left", sortField: "name" },
  { title: "Code postal", className: "text-left" },
  { title: "Analyse d'arrêté", className: "text-left", sortField: "details.options.decreeAnalysis.enabled" },
  { title: "Intégration carte", className: "text-left", sortField: "details.options.mapIntegration" },
  { title: "Application mobile", className: "text-left", sortField: "details.options.mobileMap" },
  { title: "Action", className: "text-center" },
];

const StatusBadge = ({ enabled }: { enabled?: boolean | null }) => {
  if (enabled == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return enabled ? (
    <Badge className="bg-green-100 text-green-700 border-transparent">Activée</Badge>
  ) : (
    <Badge className="bg-red-100 text-red-700 border-transparent">Désactivée</Badge>
  );
};

export const CityTable = ({ currentPage, setCurrentPage, cityName }: PaginatedCitiesProps) => {
  const [selectedCity, setSelectedCity] = useState<City>();
  const { cities, loading, deleteCity } = usePaginatedCitiesActions({
    setCurrentPage,
    currentPage,
    cityName,
  });

  const handleSort = (field: string) => {
    const isActive = currentPage.sortField === field;
    const sortOrder = isActive && currentPage.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc;
    setCurrentPage({ ...currentPage, sortField: field, sortOrder, pageIndex: 1 });
  };
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);

  const handleRemove = async () => {
    if (selectedCity) {
      await deleteCity({ variables: { cityId: selectedCity.id } });
      setOpenConfirm(false);
    }
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSelectedCity(undefined);
  };

  const onChangePage = (page: number) => {
    setCurrentPage({ ...currentPage, pageIndex: page });
  };

  return (
    <>
      <div className="flex flex-col w-full gap-y-2 h-full overflow-y-auto">
        <div className="w-full bottom-2 pb-8 h-full">
          <Table>
            <TableHeader>
              <TableRow>
                {HeaderCells.map((item, idx) => {
                  const { sortField } = item;
                  const SortArrow = sortField
                    ? currentPage.sortField === sortField
                      ? currentPage.sortOrder === SortOrder.Desc
                        ? ArrowDown
                        : ArrowUp
                      : ArrowUpDown
                    : null;
                  return (
                    <TableHead className={item.className} key={`city-table-header-${idx}`}>
                      {sortField && SortArrow ? (
                        <button
                          type="button"
                          onClick={() => handleSort(sortField)}
                          className="flex items-center gap-1 cursor-pointer bg-transparent border-none p-0"
                        >
                          <span>{item.title}</span>
                          <SortArrow className="h-4 w-4" />
                        </button>
                      ) : (
                        item.title
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody className="min-h-fit  max-h-full">
              {loading ? (
                <TableSkeleton />
              ) : (
                <>
                  {isEmpty(cities?.paginatedCities.results) ? (
                    <GenNoRecord colSpan={HeaderCells.length} message={loading ? "Loading..." : "No results found."} />
                  ) : (
                    cities?.paginatedCities.results.map((item: City, idx: number) => (
                      <TableRow
                        className="bg-white align-middle cursor-pointer p-4 hover:none"
                        key={`city-list-${idx}`}
                      >
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="font-medium">{item.zipCode}</TableCell>
                        <TableCell>
                          <StatusBadge enabled={item?.details?.options?.decreeAnalysis?.enabled} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge enabled={item?.details?.options?.mapIntegration} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge enabled={item?.details?.options?.mobileMap} />
                        </TableCell>
                        <TableCell className="flex flex-row gap-x-2 justify-center">
                          <IconButton
                            onClick={() => {
                              setSelectedCity(item);
                              setOpenEdit(true);
                            }}
                            className="border-transparent text-secondary rounded-md w-10 h-10 hover:bg-secondary bg-primary-active p-0 m-0"
                          >
                            <EditIcon className="h-5 w-5" stroke="black" />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setOpenConfirm(true);
                              setSelectedCity(item);
                            }}
                            className="border-transparent  rounded-md w-10 h-10 hover:bg-secondary  p-0 m-0"
                          >
                            <DeleteIcon className="h-5 w-5" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="mb-2 flex flex-col items-end">
          {cities && (
            <Pagination
              onChangePage={onChangePage}
              currentPage={currentPage.pageIndex}
              totalPage={cities.paginatedCities.totalPages}
            />
          )}
        </div>
      </div>
      <AlertDialog open={openConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete city</AlertDialogTitle>
            <AlertDialogDescription>Are you sure that you want to delete this city?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCloseConfirm}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {openEdit && selectedCity && (
        <AddCity
          onDismiss={setOpenEdit}
          open={openEdit}
          city={selectedCity}
          {...{ cityName, setCurrentPage, currentPage }}
        />
      )}
    </>
  );
};

interface GenNoRecordProps {
  colSpan?: number;
  message?: string;
}

export const GenNoRecord = ({ colSpan = 3, message = "No results found" }: GenNoRecordProps) => {
  return (
    <tr className="bg-default">
      <td className="px-6 py-4 whitespace-nowrap bg-white	 text-primary" colSpan={colSpan}>
        <div className="flex items-center justify-center">{message}</div>
      </td>
    </tr>
  );
};
